import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlay,
    faPause,
    faArrowUpRightFromSquare,
    faComment,
} from '@fortawesome/free-solid-svg-icons';
import GradientThumb from './GradientThumb';
import Helpers from '../Helpers';
import EditableName from './EditableName';
import Activity from '../models/Activity';
import firebase from '../firebaseConfig';
import { relativeTime, senderInitials } from '../utils';
import { useAlbumArtURL } from '../hooks/useAlbumArtURL';

function projectFirebaseRef(project) {
    if (project?.getFirebaseRef) return project.getFirebaseRef();
    return firebase.database()
        .ref('artists').child(project.artistId)
        .child('projects').child(project.id);
}

const isShare = (type) => typeof type === 'string' && type.startsWith('shared-');

const MiddlePanel = ({ project, tracks, onPlay, onRenameProject, readOnly, isPlaying }) => {
    const [activity, setActivity] = useState([]);
    const [labels, setLabels] = useState({});

    useEffect(() => {
        if (!project) {
            setActivity([]);
            setLabels({});
            return;
        }
        let cancelled = false;
        let liveQuery = null;
        let liveHandler = null;
        const seenIds = new Set();
        setLabels({});
        setActivity([]);

        const resolveLabel = (a) => {
            a.getLabel().then((label) => {
                if (cancelled) return;
                setLabels((prev) => ({ ...prev, [a.id]: label }));
            }).catch(() => { /* skeleton stays */ });
        };

        const ingest = async (id) => {
            if (cancelled || seenIds.has(id)) return;
            seenIds.add(id);
            try {
                const a = await Activity.getActivityById(id);
                if (cancelled) return;
                a.project = project;
                setActivity((prev) => {
                    if (prev.some((p) => p.id === id)) return prev;
                    const next = [a, ...prev];
                    next.sort((x, y) => (y.timeCreated || 0) - (x.timeCreated || 0));
                    return next;
                });
                resolveLabel(a);
            } catch { /* ignore single failure */ }
        };

        (async () => {
            try {
                const projectRef = projectFirebaseRef(project);
                const snap = await projectRef
                    .child('activity')
                    .orderByChild('time created')
                    .limitToLast(20)
                    .once('value');
                if (cancelled) return;
                const ids = Object.keys(snap.val() || {});
                const items = (await Promise.all(ids.map(async (id) => {
                    try {
                        const a = await Activity.getActivityById(id);
                        a.project = project;
                        return a;
                    } catch { return null; }
                }))).filter(Boolean).reverse();
                if (cancelled) return;
                items.forEach((a) => seenIds.add(a.id));
                setActivity(items);

                const resolved = await Promise.all(items.map(async (a) => {
                    try { return [a.id, await a.getLabel()]; }
                    catch { return [a.id, null]; }
                }));
                if (cancelled) return;
                setLabels((prev) => {
                    const next = { ...prev };
                    for (const [id, label] of resolved) {
                        if (label) next[id] = label;
                    }
                    return next;
                });

                liveQuery = projectRef
                    .child('activity')
                    .orderByChild('time created')
                    .limitToLast(50);

                liveHandler = (snap2) => { ingest(snap2.key); };
                liveQuery.on('child_added', liveHandler);
            } catch {
                if (!cancelled) setActivity([]);
            }
        })();

        return () => {
            cancelled = true;
            if (liveQuery && liveHandler) liveQuery.off('child_added', liveHandler);
        };
    }, [project]);

    const imageUrl = useAlbumArtURL(project);

    if (!project) {
        return <div className="middle-panel empty">Select a project</div>;
    }

    const trackCount = tracks.length || project.numberOfTracks || 0;
    const duration = tracks.length
        ? tracks.reduce((acc, t) => acc + (t?.duration || 0), 0)
        : (project.duration || 0);

    return (
        <section className="middle-panel" key={project.id}>
            <GradientThumb id={project.id} imageUrl={imageUrl} className="middle-art" />

            <div className="middle-info">
                <div className="middle-info-text">
                    <h1 className="middle-title">
                        {readOnly ? (
                            <span>{project.name || 'Untitled'}</span>
                        ) : (
                            <EditableName
                                value={project.name}
                                placeholder="Untitled"
                                onSave={(name) => onRenameProject?.(project, name)}
                            />
                        )}
                    </h1>
                    <div className="middle-meta">
                        {trackCount} tracks · {Helpers.formatDuration(duration || 0)}
                        {project.artist?.name ? ` · ${project.artist.name}` : ''}
                    </div>
                </div>
                <div className="middle-actions">
                    <button
                        className="action-btn primary"
                        title={isPlaying ? 'Pause' : 'Play'}
                        onClick={onPlay}
                    >
                        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                    </button>
                </div>
            </div>

            {!readOnly && <div className="middle-activity">
                <div className="middle-activity-header">
                    <span>ACTIVITY</span>
                </div>
                {activity.length === 0 && <div className="middle-empty">No activity yet.</div>}
                <div className="middle-activity-list">
                    {activity.map((a) => {
                        const senderName = a.senderName || 'Someone';
                        const time = relativeTime(a.timeCreated);
                        const share = isShare(a.notificationType);
                        return (
                            <div key={a.id} className="middle-activity-row">
                                <div className={`middle-activity-icon ${share ? 'share' : 'comment'}`}>
                                    <FontAwesomeIcon icon={share ? faArrowUpRightFromSquare : faComment} />
                                </div>
                                <div className="middle-activity-avatar">{senderInitials(senderName)}</div>
                                <div className="middle-activity-body">
                                    <div className="middle-activity-line">
                                        <span className="middle-activity-name">{senderName}</span>
                                        <span className="middle-activity-time">{time}</span>
                                    </div>
                                    {labels[a.id] ? (
                                    <div className="middle-activity-text">{labels[a.id]}</div>
                                ) : (
                                    <div className="middle-activity-skeleton" aria-hidden="true">
                                        <span className="skeleton-line" />
                                        <span className="skeleton-line short" />
                                    </div>
                                )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>}
        </section>
    );
};

export default MiddlePanel;
